import { ClipboardEvent, useMemo, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Combobox } from '@/components/core/Combobox';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { Textarea } from '@/components/core/Textarea';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { ServerErrorResponse } from '@/types/ServerErrorResponse';

import {
    ERROR_CODE_BULK_SEARCH_LONG_VALUE,
    ERROR_CODE_BULK_SEARCH_STILL_ACTIVE,
    fetchBulkSearch,
    useCreateBulkSearch,
} from './bulkSearch.queries';
import { persistCurrentBulkSearch } from './bulkSearchSession';
import {
    BulkSearch,
    BulkSearchDialogState,
    BulkSearchMatchPrecision,
} from './bulkSearch.types';
import { buildBulkSearchName, prepareQueryValues } from './prepareQueryValues';

const FILTER_LAYOUT = 'filter';

interface AttributeOption {
    value: string;
    label: string;
    name: string;
}

interface BulkSearchDialogProps {
    open: boolean;
    initialState?: BulkSearchDialogState;
    onClose: () => void;
    onStarted: (bulkSearch: BulkSearch) => void;
}

function insertPastedLines(
    current: string,
    pasted: string,
    selectionStart: number,
    selectionEnd: number,
): string {
    const rows = pasted.trim().match(/[^\n\r]+/g) ?? [];
    const modified = rows.join('\n');
    const start = Math.max(0, selectionStart);
    const end = Math.max(start, selectionEnd);
    return `${current.slice(0, start)}${modified}${current.slice(end)}`;
}

export function BulkSearchDialog({
    open,
    initialState,
    onClose,
    onStarted,
}: BulkSearchDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const { organization } = useOrganization();

    const [attributeName, setAttributeName] = useState('');
    const [matchPrecision, setMatchPrecision] = useState<BulkSearchMatchPrecision>('exact');
    const [queryValues, setQueryValues] = useState(initialState?.queryValues ?? '');

    const usesOpenSearch = Boolean(organization?.usesOpenSearchIndex);
    const queryTooLong = initialState?.queryTooLong ?? 0;

    const attributeOptions = useMemo<AttributeOption[]>(() => {
        let attributes = dataModel?.filteredLayoutAttributes(FILTER_LAYOUT) ?? [];
        if (attributes.length === 0) {
            attributes = dataModel?.allAttributes() ?? [];
        }
        return attributes.map((attribute) => ({
            value: attribute.name,
            label: attribute.translatedLabel || attribute.label || attribute.name,
            name: attribute.name,
        }));
    }, [dataModel]);

    const selectedOption = attributeOptions.find((option) => option.value === attributeName)
        ?? null;

    const { mutate: createBulkSearch, isPending } = useCreateBulkSearch({
        onSuccess: (bulkSearch) => {
            persistCurrentBulkSearch(bulkSearch);
            onStarted(bulkSearch);
            onClose();
        },
        onError: async (error: ServerErrorResponse) => {
            const errorCode = error.response?.data?.errorCode;
            const message = error.response?.data?.message;

            if (errorCode === ERROR_CODE_BULK_SEARCH_STILL_ACTIVE) {
                toast.warning(message || t('BULK_SEARCH.STILL_ACTIVE'));
                const activeId = error.response?.data?.parameters?.activeBulkSearchId;
                if (activeId) {
                    try {
                        const existing = await fetchBulkSearch(activeId);
                        persistCurrentBulkSearch(existing);
                        onStarted(existing);
                        onClose();
                    } catch {
                        toast.error(t('BULK_SEARCH.ERROR'));
                    }
                }
                return;
            }

            if (errorCode === ERROR_CODE_BULK_SEARCH_LONG_VALUE) {
                toast.error(message || t('BULK_SEARCH.LONG_VALUE'));
                return;
            }

            toast.error(t('BULK_SEARCH.ERROR'));
        },
    });

    const canStart = Boolean(attributeName && queryValues.trim()) && !isPending;

    const handleStart = () => {
        const searchValues = prepareQueryValues(queryValues);
        if (!attributeName || searchValues.length === 0) {
            return;
        }
        createBulkSearch({
            name: buildBulkSearchName(),
            attributeName,
            exactSearch: matchPrecision === 'exact',
            searchValues,
        });
    };

    const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
        const pasted = event.clipboardData.getData('text/plain')
            || event.clipboardData.getData('Text');
        if (!pasted.trim()) {
            return;
        }

        event.preventDefault();
        const target = event.currentTarget;
        setQueryValues(
            insertPastedLines(
                queryValues,
                pasted,
                target.selectionStart ?? queryValues.length,
                target.selectionEnd ?? queryValues.length,
            ),
        );
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
            <DialogContent
                variant="small"
                className="bulk-search-modal"
                onInteractOutside={(event) => {
                    const node = event.target as HTMLElement | null;
                    if (node?.closest('[class*="react-select"]')) {
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogHeaderDefault>{t('BULK_SEARCH')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody>
                    {queryTooLong > 0 && (
                        <Callout variant="warning" className="tw-mb-4">
                            {t('BULK_SEARCH.QUERY_TOO_LONG', { queryTooLong })}
                        </Callout>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <div>
                            <label
                                htmlFor="bulk-search-attribute"
                                className="tw-block tw-mb-1 tw-text-t-sm tw-font-medium tw-text-t-primary"
                            >
                                {t('BULK_SEARCH.ATTRIBUTE')}
                            </label>
                            <Combobox
                                inputId="bulk-search-attribute"
                                options={attributeOptions}
                                value={selectedOption}
                                placeholder={t('CHOOSE_ATTRIBUTE')}
                                onChange={(option: AttributeOption | null) => {
                                    setAttributeName(option?.value ?? '');
                                }}
                                formatOptionLabel={(option: AttributeOption) => (
                                    <div>
                                        <strong>{option.label}</strong>
                                        <div className="tw-text-t-xs tw-text-t-secondary">
                                            {option.name}
                                        </div>
                                    </div>
                                )}
                                filterOption={(option, input) => {
                                    const query = input.toLowerCase();
                                    const data = option.data as AttributeOption;
                                    return (
                                        option.label.toLowerCase().includes(query)
                                        || data.name.toLowerCase().includes(query)
                                    );
                                }}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 1100 }),
                                }}
                            />
                        </div>
                        <div>
                            <div className="tw-block tw-mb-2 tw-text-t-sm tw-font-medium tw-text-t-primary">
                                {t('BULK_SEARCH.MATCH_PRECISION')}
                            </div>
                            <RadioGroup
                                value={matchPrecision}
                                onValueChange={(value) => {
                                    setMatchPrecision(value as BulkSearchMatchPrecision);
                                }}
                                className="tw-flex tw-flex-row tw-flex-wrap tw-gap-4"
                            >
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <RadioGroupItem value="exact" id="bulk-search-match-exact" />
                                    <label
                                        htmlFor="bulk-search-match-exact"
                                        className="tw-text-t-sm tw-text-t-primary tw-m-0"
                                    >
                                        {t('BULK_SEARCH.MATCH_EXACT')}
                                    </label>
                                </div>
                                {usesOpenSearch ? (
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <RadioGroupItem
                                            value="infix"
                                            id="bulk-search-match-infix"
                                        />
                                        <label
                                            htmlFor="bulk-search-match-infix"
                                            className="tw-text-t-sm tw-text-t-primary tw-m-0"
                                        >
                                            {t('BULK_SEARCH.MATCH_INFIX')}
                                        </label>
                                    </div>
                                ) : (
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <RadioGroupItem
                                            value="prefix"
                                            id="bulk-search-match-prefix"
                                        />
                                        <label
                                            htmlFor="bulk-search-match-prefix"
                                            className="tw-text-t-sm tw-text-t-primary tw-m-0"
                                        >
                                            {t('BULK_SEARCH.MATCH_PREFIX')}
                                        </label>
                                    </div>
                                )}
                            </RadioGroup>
                        </div>
                        <div>
                            <label
                                htmlFor="query-values"
                                className="tw-block tw-mb-1 tw-text-t-sm tw-font-medium tw-text-t-primary"
                            >
                                {t('BULK_SEARCH.VALUES')}
                            </label>
                            <Textarea
                                id="query-values"
                                rows={6}
                                value={queryValues}
                                placeholder={t('BULK_SEARCH.PLACEHOLDER')}
                                onChange={(event) => setQueryValues(event.target.value)}
                                onPaste={handlePaste}
                            />
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button
                        id="startBulkSearchButton"
                        type="button"
                        disabled={!canStart}
                        loading={isPending}
                        onClick={handleStart}
                    >
                        {t('BULK_SEARCH.START')}
                    </Button>
                    <Button
                        type="button"
                        variant="secondary"
                        disabled={isPending}
                        onClick={onClose}
                    >
                        {t('CANCEL')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
